export interface Point {
  x: number;
  y: number;
}

export type WhiteboardTool =
  // General
  | 'pen' | 'highlighter' | 'eraser' | 'line' | 'arrow' | 'rect' | 'circle' | 'text' | 'sticky'
  // Database / ERD
  | 'db_table' | 'db_relation' | 'db_attr'
  // Electronics
  | 'el_resistor' | 'el_capacitor' | 'el_inductor' | 'el_ground' | 'el_voltage' | 'el_switch' | 'el_diode'
  | 'el_and' | 'el_or' | 'el_not' | 'el_xor'
  // Flowchart
  | 'fc_process' | 'fc_decision' | 'fc_terminal' | 'fc_io'
  // Math & Science
  | 'math_grid' | 'math_axes' | 'math_formula';

export interface WhiteboardElement {
  id: string;
  tool: WhiteboardTool;
  color: string;
  size: number;
  points?: Point[];
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  text?: string;
  extra?: Record<string, any>;
}

export interface WhiteboardPage {
  id: string;
  pageNumber: number;
  title: string;
  elements: WhiteboardElement[];
}

/**
 * Render vector elements onto any Canvas 2D context at any scale.
 */
export function renderElementsToCanvas(
  ctx: CanvasRenderingContext2D,
  elements: WhiteboardElement[],
  scale = 1
) {
  for (const el of elements) {
    ctx.save();
    ctx.lineWidth = Math.max(1, el.size * scale);
    ctx.strokeStyle = el.color;
    ctx.fillStyle = el.color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (el.tool) {
      case 'pen': {
        if (el.points && el.points.length > 0) {
          ctx.beginPath();
          ctx.moveTo(el.points[0].x * scale, el.points[0].y * scale);
          for (let i = 1; i < el.points.length; i++) {
            ctx.lineTo(el.points[i].x * scale, el.points[i].y * scale);
          }
          ctx.stroke();
        }
        break;
      }

      case 'highlighter': {
        if (el.points && el.points.length > 0) {
          ctx.globalAlpha = 0.35;
          ctx.lineWidth = el.size * 2.5 * scale;
          ctx.beginPath();
          ctx.moveTo(el.points[0].x * scale, el.points[0].y * scale);
          for (let i = 1; i < el.points.length; i++) {
            ctx.lineTo(el.points[i].x * scale, el.points[i].y * scale);
          }
          ctx.stroke();
        }
        break;
      }

      case 'eraser': {
        if (el.points && el.points.length > 0) {
          ctx.globalCompositeOperation = 'destination-out';
          ctx.lineWidth = el.size * 3 * scale;
          ctx.beginPath();
          ctx.moveTo(el.points[0].x * scale, el.points[0].y * scale);
          for (let i = 1; i < el.points.length; i++) {
            ctx.lineTo(el.points[i].x * scale, el.points[i].y * scale);
          }
          ctx.stroke();
        }
        break;
      }

      case 'line': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          ctx.beginPath();
          ctx.moveTo(el.startX * scale, el.startY * scale);
          ctx.lineTo(el.endX * scale, el.endY * scale);
          ctx.stroke();
        }
        break;
      }

      case 'arrow': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          const sX = el.startX * scale;
          const sY = el.startY * scale;
          const eX = el.endX * scale;
          const eY = el.endY * scale;

          ctx.beginPath();
          ctx.moveTo(sX, sY);
          ctx.lineTo(eX, eY);
          ctx.stroke();

          // Arrowhead
          const angle = Math.atan2(eY - sY, eX - sX);
          const headLen = 14 * scale;
          ctx.beginPath();
          ctx.moveTo(eX, eY);
          ctx.lineTo(eX - headLen * Math.cos(angle - Math.PI / 6), eY - headLen * Math.sin(angle - Math.PI / 6));
          ctx.lineTo(eX - headLen * Math.cos(angle + Math.PI / 6), eY - headLen * Math.sin(angle + Math.PI / 6));
          ctx.closePath();
          ctx.fill();
        }
        break;
      }

      case 'rect': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          const x = Math.min(el.startX, el.endX) * scale;
          const y = Math.min(el.startY, el.endY) * scale;
          const w = Math.abs(el.endX - el.startX) * scale;
          const h = Math.abs(el.endY - el.startY) * scale;
          ctx.strokeRect(x, y, w, h);
        }
        break;
      }

      case 'circle': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          const rX = (Math.abs(el.endX - el.startX) / 2) * scale;
          const rY = (Math.abs(el.endY - el.startY) / 2) * scale;
          const cX = (Math.min(el.startX, el.endX) + Math.abs(el.endX - el.startX) / 2) * scale;
          const cY = (Math.min(el.startY, el.endY) + Math.abs(el.endY - el.startY) / 2) * scale;
          ctx.beginPath();
          ctx.ellipse(cX, cY, rX, rY, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }

      case 'text': {
        if (el.startX !== undefined && el.startY !== undefined && el.text) {
          ctx.font = `${Math.max(12, (el.extra?.fontSize || 18) * scale)}px 'Plus Jakarta Sans', sans-serif`;
          ctx.fillText(el.text, el.startX * scale, el.startY * scale);
        }
        break;
      }

      case 'sticky': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const sX = el.startX * scale;
          const sY = el.startY * scale;
          const w = (el.extra?.width || 140) * scale;
          const h = (el.extra?.height || 100) * scale;

          ctx.fillStyle = el.extra?.bgColor || '#fef08a';
          ctx.shadowColor = 'rgba(0,0,0,0.3)';
          ctx.shadowBlur = 8 * scale;
          ctx.fillRect(sX, sY, w, h);
          ctx.shadowBlur = 0;

          ctx.fillStyle = '#1e293b';
          ctx.font = `bold ${12 * scale}px sans-serif`;
          ctx.fillText(el.extra?.title || 'NOTE', sX + 10 * scale, sY + 20 * scale);

          ctx.font = `${11 * scale}px sans-serif`;
          ctx.fillText(el.text || 'Add thoughts...', sX + 10 * scale, sY + 40 * scale);
        }
        break;
      }

      // --- DATABASE / ERD ---
      case 'db_table': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = (el.extra?.width || 180) * scale;
          const tableName = el.extra?.tableName || el.text || 'users_table';
          const columns: string[] = el.extra?.columns || [
            '🔑 id: INT [PK]',
            '📄 email: VARCHAR(255)',
            '🔒 password_hash: TEXT',
            '🕒 created_at: TIMESTAMP'
          ];
          const rowH = 22 * scale;
          const headerH = 28 * scale;
          const totalH = headerH + columns.length * rowH + 6 * scale;

          // Box container
          ctx.fillStyle = '#1e2230';
          ctx.strokeStyle = el.color || '#38bdf8';
          ctx.lineWidth = 2 * scale;
          ctx.fillRect(x, y, w, totalH);
          ctx.strokeRect(x, y, w, totalH);

          // Header
          ctx.fillStyle = '#0284c7';
          ctx.fillRect(x, y, w, headerH);
          ctx.fillStyle = '#ffffff';
          ctx.font = `bold ${12 * scale}px 'Plus Jakarta Sans', monospace`;
          ctx.fillText(`🗄️ ${tableName}`, x + 8 * scale, y + 18 * scale);

          // Columns
          ctx.font = `${10 * scale}px 'JetBrains Mono', monospace`;
          for (let i = 0; i < columns.length; i++) {
            const rowY = y + headerH + (i + 1) * rowH - 6 * scale;
            ctx.fillStyle = columns[i].includes('🔑') ? '#fbbf24' : '#cbd5e1';
            ctx.fillText(columns[i], x + 10 * scale, rowY);
          }
        }
        break;
      }

      case 'db_relation': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          const sX = el.startX * scale;
          const sY = el.startY * scale;
          const eX = el.endX * scale;
          const eY = el.endY * scale;

          // Orthogonal or direct connector
          ctx.strokeStyle = el.color || '#38bdf8';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.moveTo(sX, sY);
          ctx.lineTo(eX, eY);
          ctx.stroke();

          // 1-to-Many Crow's foot indicator at end
          const angle = Math.atan2(eY - sY, eX - sX);
          const barbLen = 12 * scale;
          ctx.beginPath();
          ctx.moveTo(eX, eY);
          ctx.lineTo(eX - barbLen * Math.cos(angle - Math.PI / 4), eY - barbLen * Math.sin(angle - Math.PI / 4));
          ctx.moveTo(eX, eY);
          ctx.lineTo(eX - barbLen * Math.cos(angle + Math.PI / 4), eY - barbLen * Math.sin(angle + Math.PI / 4));
          ctx.stroke();

          // Relation label
          ctx.fillStyle = '#38bdf8';
          ctx.font = `${10 * scale}px sans-serif`;
          ctx.fillText('1..*', (sX + eX) / 2, (sY + eY) / 2 - 6 * scale);
        }
        break;
      }

      case 'db_attr': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const text = el.text || 'attribute';
          ctx.font = `${11 * scale}px sans-serif`;
          const textW = ctx.measureText(text).width;
          const pad = 12 * scale;
          const pillW = textW + pad * 2;
          const pillH = 24 * scale;

          ctx.fillStyle = '#0f172a';
          ctx.strokeStyle = el.color || '#a855f7';
          ctx.lineWidth = 1.5 * scale;
          ctx.beginPath();
          ctx.roundRect(x, y, pillW, pillH, 12 * scale);
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = '#e2e8f0';
          ctx.fillText(text, x + pad, y + 16 * scale);
        }
        break;
      }

      // --- ELECTRONICS & LOGIC CIRCUITS LAB ---
      case 'el_resistor': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const len = 70 * scale;
          const amp = 10 * scale;

          ctx.strokeStyle = el.color || '#34d399';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + 12 * scale, y);
          // Zig-zag
          ctx.lineTo(x + 18 * scale, y - amp);
          ctx.lineTo(x + 28 * scale, y + amp);
          ctx.lineTo(x + 38 * scale, y - amp);
          ctx.lineTo(x + 48 * scale, y + amp);
          ctx.lineTo(x + 58 * scale, y);
          ctx.lineTo(x + len, y);
          ctx.stroke();

          ctx.fillStyle = '#34d399';
          ctx.font = `${10 * scale}px monospace`;
          ctx.fillText(el.text || 'R = 10kΩ', x + 15 * scale, y - amp - 4 * scale);
        }
        break;
      }

      case 'el_capacitor': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const h = 24 * scale;

          ctx.strokeStyle = el.color || '#38bdf8';
          ctx.lineWidth = 2 * scale;
          // Left wire & plate
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + 20 * scale, y);
          ctx.moveTo(x + 20 * scale, y - h / 2);
          ctx.lineTo(x + 20 * scale, y + h / 2);
          // Right plate & wire
          ctx.moveTo(x + 30 * scale, y - h / 2);
          ctx.lineTo(x + 30 * scale, y + h / 2);
          ctx.moveTo(x + 30 * scale, y);
          ctx.lineTo(x + 50 * scale, y);
          ctx.stroke();

          ctx.fillStyle = '#38bdf8';
          ctx.font = `${10 * scale}px monospace`;
          ctx.fillText(el.text || 'C = 100μF', x + 8 * scale, y - h / 2 - 4 * scale);
        }
        break;
      }

      case 'el_ground': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;

          ctx.strokeStyle = el.color || '#cbd5e1';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + 16 * scale);
          // 3 bars
          ctx.moveTo(x - 14 * scale, y + 16 * scale);
          ctx.lineTo(x + 14 * scale, y + 16 * scale);
          ctx.moveTo(x - 9 * scale, y + 21 * scale);
          ctx.lineTo(x + 9 * scale, y + 21 * scale);
          ctx.moveTo(x - 4 * scale, y + 26 * scale);
          ctx.lineTo(x + 4 * scale, y + 26 * scale);
          ctx.stroke();

          ctx.fillStyle = '#cbd5e1';
          ctx.font = `${9 * scale}px monospace`;
          ctx.fillText('GND', x + 18 * scale, y + 20 * scale);
        }
        break;
      }

      case 'el_voltage': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const r = 16 * scale;

          ctx.strokeStyle = el.color || '#f59e0b';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.stroke();

          ctx.fillStyle = '#f59e0b';
          ctx.font = `bold ${12 * scale}px monospace`;
          ctx.fillText('+', x - 5 * scale, y - 4 * scale);
          ctx.fillText('−', x - 4 * scale, y + 10 * scale);
          ctx.font = `${10 * scale}px monospace`;
          ctx.fillText(el.text || 'Vcc 5V', x + r + 6 * scale, y + 4 * scale);
        }
        break;
      }

      case 'el_and': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = 40 * scale;
          const h = 32 * scale;

          ctx.strokeStyle = el.color || '#a855f7';
          ctx.lineWidth = 2 * scale;
          // Inputs
          ctx.beginPath();
          ctx.moveTo(x - 12 * scale, y + 8 * scale);
          ctx.lineTo(x, y + 8 * scale);
          ctx.moveTo(x - 12 * scale, y + h - 8 * scale);
          ctx.lineTo(x, y + h - 8 * scale);
          // AND body
          ctx.moveTo(x, y);
          ctx.lineTo(x + w / 2, y);
          ctx.arc(x + w / 2, y + h / 2, h / 2, -Math.PI / 2, Math.PI / 2);
          ctx.lineTo(x, y + h);
          ctx.closePath();
          // Output
          ctx.moveTo(x + w / 2 + h / 2, y + h / 2);
          ctx.lineTo(x + w / 2 + h / 2 + 14 * scale, y + h / 2);
          ctx.stroke();

          ctx.fillStyle = '#a855f7';
          ctx.font = `bold ${9 * scale}px monospace`;
          ctx.fillText('AND', x + 8 * scale, y + h / 2 + 3 * scale);
        }
        break;
      }

      case 'el_or': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = 42 * scale;
          const h = 32 * scale;

          ctx.strokeStyle = el.color || '#ec4899';
          ctx.lineWidth = 2 * scale;
          // Inputs
          ctx.beginPath();
          ctx.moveTo(x - 10 * scale, y + 8 * scale);
          ctx.lineTo(x + 4 * scale, y + 8 * scale);
          ctx.moveTo(x - 10 * scale, y + h - 8 * scale);
          ctx.lineTo(x + 4 * scale, y + h - 8 * scale);
          // OR shape
          ctx.moveTo(x, y);
          ctx.quadraticCurveTo(x + w * 0.7, y, x + w, y + h / 2);
          ctx.quadraticCurveTo(x + w * 0.7, y + h, x, y + h);
          ctx.quadraticCurveTo(x + 10 * scale, y + h / 2, x, y);
          // Output
          ctx.moveTo(x + w, y + h / 2);
          ctx.lineTo(x + w + 14 * scale, y + h / 2);
          ctx.stroke();

          ctx.fillStyle = '#ec4899';
          ctx.font = `bold ${9 * scale}px monospace`;
          ctx.fillText('OR', x + 14 * scale, y + h / 2 + 3 * scale);
        }
        break;
      }

      case 'el_not': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = 30 * scale;
          const h = 26 * scale;

          ctx.strokeStyle = el.color || '#ef4444';
          ctx.lineWidth = 2 * scale;
          // Input
          ctx.beginPath();
          ctx.moveTo(x - 10 * scale, y + h / 2);
          ctx.lineTo(x, y + h / 2);
          // Triangle
          ctx.moveTo(x, y);
          ctx.lineTo(x + w, y + h / 2);
          ctx.lineTo(x, y + h);
          ctx.closePath();
          ctx.stroke();
          // Inversion bubble
          ctx.beginPath();
          ctx.arc(x + w + 4 * scale, y + h / 2, 4 * scale, 0, Math.PI * 2);
          ctx.stroke();
          // Output
          ctx.beginPath();
          ctx.moveTo(x + w + 8 * scale, y + h / 2);
          ctx.lineTo(x + w + 18 * scale, y + h / 2);
          ctx.stroke();
        }
        break;
      }

      // --- FLOWCHART & SYSTEMS ---
      case 'fc_process': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = (el.extra?.width || 130) * scale;
          const h = (el.extra?.height || 50) * scale;

          ctx.fillStyle = '#1e293b';
          ctx.strokeStyle = el.color || '#3b82f6';
          ctx.lineWidth = 2 * scale;
          ctx.fillRect(x, y, w, h);
          ctx.strokeRect(x, y, w, h);

          ctx.fillStyle = '#f8fafc';
          ctx.font = `${11 * scale}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(el.text || 'Process Step', x + w / 2, y + h / 2 + 4 * scale);
          ctx.textAlign = 'start';
        }
        break;
      }

      case 'fc_decision': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = (el.extra?.width || 120) * scale;
          const h = (el.extra?.height || 60) * scale;

          ctx.fillStyle = '#1e293b';
          ctx.strokeStyle = el.color || '#f59e0b';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.moveTo(x + w / 2, y);
          ctx.lineTo(x + w, y + h / 2);
          ctx.lineTo(x + w / 2, y + h);
          ctx.lineTo(x, y + h / 2);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = '#f8fafc';
          ctx.font = `${10 * scale}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(el.text || 'Condition?', x + w / 2, y + h / 2 + 3 * scale);
          ctx.textAlign = 'start';
        }
        break;
      }

      case 'fc_terminal': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const w = (el.extra?.width || 110) * scale;
          const h = (el.extra?.height || 42) * scale;

          ctx.fillStyle = '#1e293b';
          ctx.strokeStyle = el.color || '#10b981';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.roundRect(x, y, w, h, 21 * scale);
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = '#f8fafc';
          ctx.font = `bold ${11 * scale}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(el.text || 'START / END', x + w / 2, y + h / 2 + 4 * scale);
          ctx.textAlign = 'start';
        }
        break;
      }

      // --- MATH & SCIENCE ---
      case 'math_grid': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const size = 160 * scale;
          const step = 20 * scale;

          ctx.strokeStyle = 'rgba(255,255,255,0.12)';
          ctx.lineWidth = 1 * scale;
          for (let gx = 0; gx <= size; gx += step) {
            ctx.beginPath();
            ctx.moveTo(x + gx, y);
            ctx.lineTo(x + gx, y + size);
            ctx.stroke();
          }
          for (let gy = 0; gy <= size; gy += step) {
            ctx.beginPath();
            ctx.moveTo(x, y + gy);
            ctx.lineTo(x + size, y + gy);
            ctx.stroke();
          }

          // Center axes
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 1.5 * scale;
          ctx.beginPath();
          ctx.moveTo(x, y + size / 2);
          ctx.lineTo(x + size, y + size / 2);
          ctx.moveTo(x + size / 2, y);
          ctx.lineTo(x + size / 2, y + size);
          ctx.stroke();
        }
        break;
      }

      case 'math_axes': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX * scale;
          const y = el.startY * scale;
          const len = 120 * scale;

          ctx.strokeStyle = el.color || '#fbbf24';
          ctx.lineWidth = 2 * scale;
          // X-axis
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + len, y);
          ctx.lineTo(x + len - 8 * scale, y - 4 * scale);
          ctx.moveTo(x + len, y);
          ctx.lineTo(x + len - 8 * scale, y + 4 * scale);
          // Y-axis
          ctx.moveTo(x, y);
          ctx.lineTo(x, y - len);
          ctx.lineTo(x - 4 * scale, y - len + 8 * scale);
          ctx.moveTo(x, y);
          ctx.lineTo(x + 4 * scale, y - len + 8 * scale);
          ctx.stroke();

          ctx.fillStyle = '#fbbf24';
          ctx.font = `italic ${12 * scale}px 'JetBrains Mono', monospace`;
          ctx.fillText('x', x + len + 4 * scale, y + 4 * scale);
          ctx.fillText('y', x - 4 * scale, y - len - 4 * scale);
        }
        break;
      }
    }

    ctx.restore();
  }
}

/**
 * Generate Infinite-Resolution SVG artifact (Crystal clear on any zoom).
 */
export function generatePageSVG(page: WhiteboardPage, width: number, height: number, roomId: string): string {
  let svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
  <style>
    text { font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif; }
    .mono { font-family: 'JetBrains Mono', monospace; }
  </style>
  <rect width="100%" height="100%" fill="#18191e"/>
  <g id="watermark" opacity="0.35">
    <text x="24" y="${height - 20}" fill="#94a3b8" font-size="13">
      Verve Whiteboard Artifact • Room: ${roomId} • Page ${page.pageNumber} (${page.title || 'Untitled'}) • Ultra Vector SVG
    </text>
  </g>
  <g id="elements">
`;

  for (const el of page.elements) {
    const col = el.color;
    const sw = el.size;

    switch (el.tool) {
      case 'pen': {
        if (el.points && el.points.length > 1) {
          const d = el.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
          svg += `    <path d="${d}" fill="none" stroke="${col}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>\n`;
        }
        break;
      }

      case 'highlighter': {
        if (el.points && el.points.length > 1) {
          const d = el.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
          svg += `    <path d="${d}" fill="none" stroke="${col}" stroke-width="${sw * 2.5}" stroke-linecap="round" opacity="0.35"/>\n`;
        }
        break;
      }

      case 'line': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          svg += `    <line x1="${el.startX}" y1="${el.startY}" x2="${el.endX}" y2="${el.endY}" stroke="${col}" stroke-width="${sw}" stroke-linecap="round"/>\n`;
        }
        break;
      }

      case 'rect': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          const x = Math.min(el.startX, el.endX);
          const y = Math.min(el.startY, el.endY);
          const w = Math.abs(el.endX - el.startX);
          const h = Math.abs(el.endY - el.startY);
          svg += `    <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="${col}" stroke-width="${sw}" rx="4"/>\n`;
        }
        break;
      }

      case 'circle': {
        if (el.startX !== undefined && el.startY !== undefined && el.endX !== undefined && el.endY !== undefined) {
          const rx = Math.abs(el.endX - el.startX) / 2;
          const ry = Math.abs(el.endY - el.startY) / 2;
          const cx = Math.min(el.startX, el.endX) + rx;
          const cy = Math.min(el.startY, el.endY) + ry;
          svg += `    <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="${col}" stroke-width="${sw}"/>\n`;
        }
        break;
      }

      case 'text': {
        if (el.startX !== undefined && el.startY !== undefined && el.text) {
          svg += `    <text x="${el.startX}" y="${el.startY}" fill="${col}" font-size="${el.extra?.fontSize || 18}">${escapeXml(el.text)}</text>\n`;
        }
        break;
      }

      case 'db_table': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX;
          const y = el.startY;
          const w = el.extra?.width || 180;
          const name = el.extra?.tableName || el.text || 'users';
          const cols: string[] = el.extra?.columns || ['🔑 id: INT', '📄 name: VARCHAR', '🕒 created_at'];
          const h = 28 + cols.length * 22 + 6;

          svg += `    <g transform="translate(${x},${y})">
      <rect width="${w}" height="${h}" fill="#1e2230" stroke="#38bdf8" stroke-width="2" rx="4"/>
      <rect width="${w}" height="28" fill="#0284c7" rx="4"/>
      <text x="8" y="19" fill="#ffffff" font-size="12" font-weight="bold">🗄️ ${escapeXml(name)}</text>
${cols.map((c, idx) => `      <text x="10" y="${28 + (idx + 1) * 22 - 6}" fill="${c.includes('🔑') ? '#fbbf24' : '#cbd5e1'}" font-size="10" class="mono">${escapeXml(c)}</text>`).join('\n')}
    </g>\n`;
        }
        break;
      }

      case 'el_resistor': {
        if (el.startX !== undefined && el.startY !== undefined) {
          const x = el.startX;
          const y = el.startY;
          svg += `    <g transform="translate(${x},${y})" stroke="#34d399" stroke-width="2" fill="none">
      <path d="M 0 0 L 12 0 L 18 -10 L 28 10 L 38 -10 L 48 10 L 58 0 L 70 0"/>
      <text x="15" y="-14" fill="#34d399" stroke="none" font-size="10" class="mono">${escapeXml(el.text || '10kΩ')}</text>
    </g>\n`;
        }
        break;
      }

      default:
        // Generic fallback element
        break;
    }
  }

  svg += `  </g>\n</svg>`;
  return svg;
}

function escapeXml(str: string): string {
  return str.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}

/**
 * Super-sampled High-Resolution Canvas Rendering (up to 8K/16K).
 */
export async function renderPageHighRes(
  page: WhiteboardPage,
  width: number,
  height: number,
  scale: number,
  format: 'png' | 'jpeg',
  roomId: string
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(width * scale);
  canvas.height = Math.floor(height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Background
  ctx.fillStyle = '#18191e';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Render elements at scale
  renderElementsToCanvas(ctx, page.elements, scale);

  // High-res footer watermark
  ctx.save();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.font = `${14 * scale}px 'Plus Jakarta Sans', sans-serif`;
  ctx.fillText(
    `Verve Whiteboard Artifact • Room: ${roomId} • Page ${page.pageNumber} of ${page.title || ''} • ${canvas.width}x${canvas.height}`,
    24 * scale,
    canvas.height - 20 * scale
  );
  ctx.restore();

  return canvas.toDataURL(format === 'jpeg' ? 'image/jpeg' : 'image/png', 0.95);
}

/**
 * Multi-Page Vector/High-Res PDF Document Generator.
 * Embeds each page cleanly in a single multi-page PDF 1.4 document.
 */
export async function generateMultiPagePDF(
  pages: WhiteboardPage[],
  width: number,
  height: number,
  roomId: string
): Promise<Blob> {
  // Convert each page to high-res JPEG image data
  const pageImages: string[] = [];
  const exportScale = Math.min(2.5, 2500 / Math.max(width, height)); // Crisp 300dpi equivalent

  for (const page of pages) {
    const dataUrl = await renderPageHighRes(page, width, height, exportScale, 'jpeg', roomId);
    pageImages.push(dataUrl);
  }

  // Construct PDF binary stream
  const pdfBytes = buildPDFFromImages(pageImages, Math.round(width * 0.75), Math.round(height * 0.75));
  return new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
}

export function buildPDFFromImages(images: string[], ptWidth: number, ptHeight: number, pixelWidth?: number, pixelHeight?: number): Uint8Array {
  const pageObjIds: number[] = [];
  let nextObjId = 1;

  // Obj 1: Catalog (will be written later)
  const catalogId = nextObjId++;
  // Obj 2: Pages tree
  const pagesTreeId = nextObjId++;

  const imageObjIds: number[] = [];
  const contentObjIds: number[] = [];

  for (let i = 0; i < images.length; i++) {
    const pageId = nextObjId++;
    pageObjIds.push(pageId);

    const imageId = nextObjId++;
    imageObjIds.push(imageId);

    const contentId = nextObjId++;
    contentObjIds.push(contentId);
  }

  // We write out objects
  const bodyParts: Uint8Array[] = [];
  const offsets: number[] = [];
  let currentOffset = 0;

  const encoder = new TextEncoder();

  function appendChunk(chunk: Uint8Array | string) {
    const data = typeof chunk === 'string' ? encoder.encode(chunk) : chunk;
    bodyParts.push(data);
    currentOffset += data.length;
  }

  appendChunk('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');

  // 1: Catalog
  offsets[catalogId] = currentOffset;
  appendChunk(`${catalogId} 0 obj\n<< /Type /Catalog /Pages ${pagesTreeId} 0 R >>\nendobj\n`);

  // 2: Pages
  offsets[pagesTreeId] = currentOffset;
  const kidsStr = pageObjIds.map(id => `${id} 0 R`).join(' ');
  appendChunk(`${pagesTreeId} 0 obj\n<< /Type /Pages /Kids [${kidsStr}] /Count ${pageObjIds.length} >>\nendobj\n`);

  // Per page objects
  for (let i = 0; i < images.length; i++) {
    const pageId = pageObjIds[i];
    const imageId = imageObjIds[i];
    const contentId = contentObjIds[i];

    // Decode base64 JPEG
    const base64Data = images[i].split(',')[1];
    const binaryImg = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));

    // Page object
    offsets[pageId] = currentOffset;
    appendChunk(
      `${pageId} 0 obj\n` +
      `<< /Type /Page\n` +
      `/Parent ${pagesTreeId} 0 R\n` +
      `/MediaBox [0 0 ${ptWidth} ${ptHeight}]\n` +
      `/Resources << /XObject << /Img${i} ${imageId} 0 R >> >>\n` +
      `/Contents ${contentId} 0 R\n` +
      `>>\nendobj\n`
    );

    // Image XObject
    offsets[imageId] = currentOffset;
    const actualWidth = pixelWidth || Math.round(ptWidth * 2);
    const actualHeight = pixelHeight || Math.round(ptHeight * 2);
    const imgHeader =
      `${imageId} 0 obj\n` +
      `<< /Type /XObject\n` +
      `/Subtype /Image\n` +
      `/Width ${actualWidth}\n` +
      `/Height ${actualHeight}\n` +
      `/ColorSpace /DeviceRGB\n` +
      `/BitsPerComponent 8\n` +
      `/Filter /DCTDecode\n` +
      `/Length ${binaryImg.length}\n` +
      `>>\nstream\n`;
    appendChunk(imgHeader);
    appendChunk(binaryImg);
    appendChunk('\nendstream\nendobj\n');

    // Content stream to paint image across the page
    offsets[contentId] = currentOffset;
    const contentStream = `q\n${ptWidth} 0 0 ${ptHeight} 0 0 cm\n/Img${i} Do\nQ\n`;
    appendChunk(
      `${contentId} 0 obj\n` +
      `<< /Length ${contentStream.length} >>\n` +
      `stream\n${contentStream}endstream\nendobj\n`
    );
  }

  // Cross reference table
  const xrefOffset = currentOffset;
  appendChunk(`xref\n0 ${nextObjId}\n0000000000 65535 f \n`);
  for (let i = 1; i < nextObjId; i++) {
    const offStr = (offsets[i] || 0).toString().padStart(10, '0');
    appendChunk(`${offStr} 00000 n \n`);
  }

  // Trailer
  appendChunk(
    `trailer\n` +
    `<< /Size ${nextObjId}\n` +
    `/Root ${catalogId} 0 R\n` +
    `>>\n` +
    `startxref\n${xrefOffset}\n%%EOF\n`
  );

  // Combine into single Uint8Array
  const totalLength = bodyParts.reduce((acc, p) => acc + p.length, 0);
  const result = new Uint8Array(totalLength);
  let pos = 0;
  for (const part of bodyParts) {
    result.set(part, pos);
    pos += part.length;
  }
  return result;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
